import { useState, useCallback, useRef } from 'react';
import { Search, FileText, Download, Printer, AlertTriangle } from 'lucide-react';
import { Spinner }   from '@/components/ui/Spinner';
import { Badge }     from '@/components/ui/Badge';
import { Card }      from '@/components/ui/Card';
import { MasterSearchModal }  from '@/components/master/MasterSearchModal';
import type { SearchColumn }  from '@/components/master/MasterSearchModal';
import { supplierApi }        from '@/api/supplier.api';
import type {
  SupplierStatementDto,
  SupplierStatementLineDto,
  SupplierListDto,
} from '@/types/supplier.types';
import { cn } from '@/utils/cn';

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function fmtAmt(n: number): string {
  if (n === 0) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtBalance(n: number): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const SEARCH_COLUMNS: SearchColumn<SupplierListDto>[] = [
  { key: 'code', label: 'Code', className: 'w-24 font-mono' },
  { key: 'name', label: 'Name' },
  { key: 'isActive', label: 'Status', className: 'w-20',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

const inputClass = cn(
  'h-8 px-3 rounded-md border text-sm',
  'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
  'border-gray-300 dark:border-gray-600',
  'focus:outline-none focus:ring-2 focus:ring-brand-500',
);

// ── Status badge ───────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
  Open:               'default',
  PartiallyAllocated: 'warning',
  FullyAllocated:     'success',
  Overpaid:           'warning',
  Cancelled:          'danger',
  Reversed:           'danger',
  WrittenOff:         'danger',
};

// ── Component ──────────────────────────────────────────────────────────────────

export const SupplierStatementPage = () => {
  // ── Filter state ────────────────────────────────────────────────────────────
  const [supplierId,   setSupplierId]   = useState<string | null>(null);
  const [supplierCode, setSupplierCode] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');
  const [currencyCode, setCurrencyCode] = useState('');

  // ── UI state ────────────────────────────────────────────────────────────────
  const [statement,   setStatement]   = useState<SupplierStatementDto | null>(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [searchOpen,  setSearchOpen]  = useState(false);

  const codeRef = useRef<HTMLInputElement>(null);

  // ── Supplier lookup ─────────────────────────────────────────────────────────
  const lookupByCode = useCallback(async (code: string) => {
    if (!code.trim()) return;
    setLoading(true); setError(null);
    try {
      const res = await supplierApi.getByCode(code.trim().toUpperCase());
      const dto = res.data.data;
      if (dto) {
        setSupplierId(dto.id);
        setSupplierCode(dto.code);
        setSupplierName(dto.name);
      } else {
        setError(`Supplier '${code.trim().toUpperCase()}' was not found.`);
        setSupplierId(null);
        setSupplierName('');
      }
    } catch {
      setError(`Supplier '${code.trim().toUpperCase()}' was not found.`);
      setSupplierId(null);
      setSupplierName('');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearchSelect = (item: SupplierListDto) => {
    setSearchOpen(false);
    setSupplierId(item.id);
    setSupplierCode(item.code);
    setSupplierName(item.name);
  };

  const fetchSuppliers = useCallback(async (search: string, page: number, pageSize: number) => {
    const res = await supplierApi.getList({ search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  }, []);

  // ── Generate statement ──────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!supplierId) { setError('Please select a supplier.'); return; }
    setLoading(true); setError(null); setStatement(null);
    try {
      const res = await supplierApi.getStatement(supplierId, {
        dateFrom:     dateFrom     || undefined,
        dateTo:       dateTo       || undefined,
        currencyCode: currencyCode || undefined,
      });
      const dto = res.data.data;
      if (!dto) {
        setError('No statement data returned.');
      } else {
        setStatement(dto);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Failed to generate statement.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full min-h-0">

      {/* Page header */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <FileText size={18} className="text-brand-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-bold text-gray-900 dark:text-gray-100">Supplier Statement</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">Chronological account statement with running balance</p>
        </div>
        <div className="flex items-center gap-2">
          {statement && (
            <>
              <button
                onClick={handlePrint}
                className={cn(
                  'h-8 px-3 flex items-center gap-1.5 text-xs rounded-md border',
                  'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400',
                  'hover:text-brand-600 hover:border-brand-400 transition-colors',
                )}
              >
                <Printer size={13} /> Print
              </button>
              <button
                className={cn(
                  'h-8 px-3 flex items-center gap-1.5 text-xs rounded-md border',
                  'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400',
                  'hover:text-brand-600 hover:border-brand-400 transition-colors',
                )}
              >
                <Download size={13} /> Export
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-end gap-3">

          {/* Supplier selector */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Supplier</label>
            <div className="flex items-center gap-1">
              <input
                ref={codeRef}
                type="text"
                value={supplierCode}
                onChange={e => setSupplierCode(e.target.value.toUpperCase())}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookupByCode(supplierCode); } }}
                placeholder="Code + Enter"
                maxLength={20}
                className={cn(inputClass, 'w-28 font-mono uppercase')}
              />
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className={cn(
                  'h-8 w-8 flex items-center justify-center rounded-md border',
                  'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800',
                  'text-gray-500 dark:text-gray-400 hover:text-brand-600 hover:border-brand-400 transition-colors',
                )}
                title="Browse suppliers"
              >
                <Search size={13} />
              </button>
              {supplierName && (
                <span className="text-xs text-gray-600 dark:text-gray-400 ml-1 max-w-48 truncate">{supplierName}</span>
              )}
            </div>
          </div>

          {/* Date range */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className={cn(inputClass, 'w-36')}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className={cn(inputClass, 'w-36')}
            />
          </div>

          {/* Currency */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Currency</label>
            <input
              type="text"
              value={currencyCode}
              onChange={e => setCurrencyCode(e.target.value.toUpperCase())}
              placeholder="All"
              maxLength={3}
              className={cn(inputClass, 'w-20 font-mono uppercase')}
            />
          </div>

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={loading || !supplierId}
            className={cn(
              'h-8 px-4 rounded-md text-xs font-semibold',
              'bg-brand-600 hover:bg-brand-700 text-white',
              'disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
            )}
          >
            {loading ? <Spinner size="sm" className="text-white" /> : 'Generate'}
          </button>

          {/* Clear */}
          {(statement || error) && (
            <button
              onClick={() => {
                setStatement(null); setError(null);
                setSupplierId(null); setSupplierCode(''); setSupplierName('');
                setDateFrom(''); setDateTo(''); setCurrencyCode('');
                setTimeout(() => codeRef.current?.focus(), 50);
              }}
              className="h-8 px-3 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 px-5 py-2.5 text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-b border-red-200 dark:border-red-800">
          <AlertTriangle size={15} className="flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !statement && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {/* Empty state */}
      {!loading && !statement && !error && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-400">
            <FileText size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">Select a supplier and click Generate</p>
            <p className="text-xs mt-1">Optionally filter by date range and currency</p>
          </div>
        </div>
      )}

      {/* Statement */}
      {statement && (
        <div className="flex-1 overflow-auto p-5 space-y-4 print:p-0">

          {/* Statement header card */}
          <Card className="print:shadow-none">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">
                  Supplier Statement
                </h2>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-0.5">
                  {statement.supplierCode} — {statement.supplierName}
                </p>
                {statement.supplierAddress && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{statement.supplierAddress}</p>
                )}
                {statement.supplierEmail && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{statement.supplierEmail}</p>
                )}
              </div>
              <div className="text-right text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                <p>Period: <span className="font-medium text-gray-700 dark:text-gray-300">
                  {statement.dateFrom ? fmtDate(statement.dateFrom) : 'All'} –{' '}
                  {statement.dateTo   ? fmtDate(statement.dateTo)   : 'All'}
                </span></p>
                {statement.currencyCode && (
                  <p>Currency: <span className="font-mono font-medium text-gray-700 dark:text-gray-300">{statement.currencyCode}</span></p>
                )}
                <p>Generated: <span className="font-medium text-gray-700 dark:text-gray-300">{fmtDate(new Date().toISOString())}</span></p>
              </div>
            </div>

            {/* Summary row */}
            <div className="mt-4 grid grid-cols-4 gap-4">
              {[
                { label: 'Opening Balance', value: fmtBalance(statement.openingBalance), color: 'text-gray-700 dark:text-gray-300' },
                { label: 'Total Payments',  value: fmtBalance(statement.totalDebits),    color: 'text-emerald-600 dark:text-emerald-400' },
                { label: 'Total Bills',     value: fmtBalance(statement.totalCredits),   color: 'text-rose-600 dark:text-rose-400' },
                { label: 'Closing Balance', value: fmtBalance(statement.closingBalance),
                  color: statement.closingBalance > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400' },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">{label}</p>
                  <p className={cn('text-lg font-bold mt-0.5 font-mono', color)}>{value}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Statement table */}
          {statement.lines.length === 0 ? (
            <div className="text-center text-gray-400 py-8 text-sm">
              No transactions found for the selected period.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    {['Date', 'Type', 'Document #', 'Reference / Narration', 'Due Date', 'Payment', 'Bill', 'Balance', 'Status'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {statement.lines.map((line: SupplierStatementLineDto, idx: number) => (
                    <tr key={`${line.openItemId}-${idx}`}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">
                        {fmtDate(line.documentDate)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={cn(
                          'inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold',
                          line.debit > 0
                            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                            : 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400',
                        )}>
                          {line.documentTypeLabel}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {line.documentNumber}
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400 max-w-48 truncate">
                        {line.reference || line.narration || '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {fmtDate(line.dueDate)}
                      </td>
                      {/* Payment column (debit side — reduces balance owed) */}
                      <td className="px-3 py-2 text-right font-mono whitespace-nowrap text-emerald-600 dark:text-emerald-400">
                        {fmtAmt(line.debit)}
                      </td>
                      {/* Bill column (credit side — increases balance owed) */}
                      <td className="px-3 py-2 text-right font-mono whitespace-nowrap text-rose-600 dark:text-rose-400">
                        {fmtAmt(line.credit)}
                      </td>
                      <td className={cn(
                        'px-3 py-2 text-right font-mono whitespace-nowrap font-semibold',
                        line.runningBalance > 0
                          ? 'text-rose-600 dark:text-rose-400'
                          : line.runningBalance < 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-gray-400',
                      )}>
                        {fmtBalance(line.runningBalance)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <Badge variant={STATUS_VARIANT[line.status] ?? 'default'} size="sm">
                          {line.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-t-2 border-gray-300 dark:border-gray-600 font-semibold">
                    <td colSpan={5} className="px-3 py-2.5 text-right text-gray-600 dark:text-gray-400">Totals</td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-600 dark:text-emerald-400">
                      {statement.totalDebits.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-rose-600 dark:text-rose-400">
                      {statement.totalCredits.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className={cn(
                      'px-3 py-2.5 text-right font-mono',
                      statement.closingBalance > 0
                        ? 'text-rose-600 dark:text-rose-400'
                        : 'text-emerald-600 dark:text-emerald-400',
                    )}>
                      {fmtBalance(statement.closingBalance)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Supplier search modal */}
      <MasterSearchModal<SupplierListDto>
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        title="Select Supplier"
        columns={SEARCH_COLUMNS}
        fetchData={fetchSuppliers}
        onSelect={handleSearchSelect}
      />
    </div>
  );
};
